/**
 * @file     SectionHeader.tsx
 * @purpose  Hub section header: optional uppercase eyebrow, an H2 on the hub
 *           display scale, and one supporting line at most 64 characters wide,
 *           with an optional trailing action (e.g. "See all"). No hooks or
 *           directive, so it renders from a Server Component or a client shell.
 */
import styles from './SectionHeader.module.css';

export interface SectionHeaderProps {
  title: React.ReactNode;
  eyebrow?: string;
  /** One supporting line. */
  description?: React.ReactNode;
  /** Id on the heading, for anchors and aria-labelledby. */
  id?: string;
  /** Heading level. Defaults to h2. */
  as?: 'h2' | 'h3';
  /** 'display' is the hub H2 scale; 'compact' suits a sub-section or a tool panel. */
  size?: 'display' | 'compact';
  /** Trailing control aligned to the heading baseline on wide screens. */
  action?: React.ReactNode;
}

export function SectionHeader({
  title,
  eyebrow,
  description,
  id,
  as = 'h2',
  size = 'display',
  action,
}: SectionHeaderProps) {
  const Heading = as;
  return (
    <div className={styles.header}>
      <div className={styles.text}>
        {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        <Heading id={id} className={`${styles.title} ${size === 'display' ? styles.display : styles.compact}`}>
          {title}
        </Heading>
        {description ? <p className={styles.description}>{description}</p> : null}
      </div>
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
}
